import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Star, Users, User, Home, Search, Activity, Shield, ListChecks, Camera, Calendar, MapPin, Lightbulb, Utensils } from "lucide-react";
import { Card } from "./ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Input } from "./ui/input";


type NavigationItem = {
  id: string;
  title: string;
  icon: string;
  url: string;
};

interface NavigationProps {
  onSearch: (e: React.FormEvent) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  className?: string;
  onNavigate?: () => void;
}

const iconMap = {
  Heart,
  Star,
  Users,
  User,
  Home,
  Activity,
  Shield,
  ListChecks,
  Camera,
  Calendar,
  MapPin,
  Lightbulb,
  Utensils,
};

export const Navigation = ({ onSearch, searchQuery, setSearchQuery, className, onNavigate }: NavigationProps) => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { data: isAdmin } = useIsAdmin();
  
  
  const { data: navigationItems, isLoading } = useQuery({
    queryKey: ['navigation-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .not('title', 'eq', 'My Profile')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as NavigationItem[];
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const handleNavigate = (url: string) => {
    navigate(url);
    if (onNavigate) {
      onNavigate();
    }
  };

  if (isLoading) {
    return (
      <Card className="card-modern p-4 space-y-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-muted rounded" />
          ))}
        </div>
      </Card>
    );
  }

  const isAuthenticated = !!profile;
  const memberOnlyTitles = ['My Profile', 'Messages', 'Bookmarks'];
  const filteredItems = navigationItems?.filter(item => {
    if (isMobile && item.title === "Likes") return false;
    if (item.title === "Home Base") return false;
    if (!isAuthenticated && memberOnlyTitles.includes(item.title)) return false;
    return true;
  }) || [];


  // Disney Guides for navigation
  const disneyGuides = [
    { title: "Disney World Tips", url: "/disney-world-tips", icon: "Lightbulb" },
    { title: "Disney Photography", url: "/disney-photography", icon: "Camera" },
    { title: "Disney Trip Planning", url: "/disney-trip-planning", icon: "Calendar" },
    { title: "Disney Attractions", url: "/disney-attractions", icon: "Star" },
    { title: "Disney Restaurants", url: "/disney-restaurants", icon: "Utensils" },
    { title: "Disney Events", url: "/disney-events", icon: "Activity" }
  ];

  return (
    <Card className={`card-modern p-4 space-y-4 ${isMobile ? 'pt-20' : ''} ${className || ''}`}>
      {isMobile && (
        <form onSubmit={onSearch} className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary" />
            <Input
              type="search"
              placeholder="Search posts, people, groups..."
              className="w-full pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </form>
      )}
      
      {filteredItems.map((item) => {
        const Icon = iconMap[item.icon as keyof typeof iconMap];
        return (
          <div
            key={item.id}
            onClick={() => handleNavigate(item.url)}
            className="flex items-center gap-3 text-foreground hover:text-foreground/90 transition-colors p-2 rounded-lg hover:bg-muted group cursor-pointer"
          >
            {Icon && (
              <div className="w-8 h-8 rounded-full bg-secondary shadow-neon-primary flex items-center justify-center transition-all duration-300 group-hover:shadow-neon-sharp">
                <Icon className="w-4 h-4 text-secondary-foreground" />
              </div>
            )}
            <span className="font-medium text-foreground">{item.title}</span>
          </div>
        );
      })}

      {/* Disney Guides Section */}
      <div className="border-t border-border pt-4 mt-4">
        <h4 className="text-sm font-semibold text-muted-foreground mb-2 px-2">Disney Guides</h4>
        {disneyGuides.map((guide) => {
          const Icon = iconMap[guide.icon as keyof typeof iconMap];
          return (
            <div
              key={guide.url}
              onClick={() => handleNavigate(guide.url)}
              className="flex items-center gap-3 text-foreground hover:text-foreground/90 transition-colors p-2 rounded-lg hover:bg-muted group cursor-pointer"
            >
              {Icon && (
                <div className="w-8 h-8 rounded-full bg-secondary shadow-neon-primary flex items-center justify-center transition-all duration-300 group-hover:shadow-neon-sharp">
                  <Icon className="w-4 h-4 text-secondary-foreground" />
                </div>
              )}
              <span className="font-medium text-foreground">{guide.title}</span>
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div
          onClick={() => handleNavigate('/admin')}
          className="flex items-center gap-3 text-foreground hover:text-foreground/90 transition-colors p-2 rounded-lg hover:bg-muted group cursor-pointer"
        >
          <div className="w-8 h-8 rounded-full bg-secondary shadow-neon-primary flex items-center justify-center transition-all duration-300 group-hover:shadow-neon-sharp">
            <Shield className="w-4 h-4 text-secondary-foreground" />
          </div>
          <span className="font-medium text-foreground">Admin</span>
        </div>
      )}
    </Card>
  );
};